import { SpringValue, useTransition } from '@react-spring/web';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { keyframes } from '@tanium/react-emotion-9';

import { GeneratorOutput } from '../../../hooks/useGeneratorOutput';
import { animationDurationMs } from '../../../utils/animationDurationMs';
import { ColorScale } from '../../../utils/ColorScale';
import { cubicBezierCurve } from '../../../utils/cubicBezierCurve';
import { DataKeyType, DefaultDatum } from '../../../utils/DataProps';
import { getEmphasisLevel } from '../../../utils/getEmphasisLevel';
import { NumberCoercible } from '../../../utils/NumberCoercible';
import { NumberScale } from '../../../utils/NumberScale';
import { PartialSeriesPoint } from '../../../utils/Series';
import { CreatePortalFn } from '../../../utils/usePortal';
import { DefaultPointType } from '../../../utils/XYDataProps';
import { getRangePoints } from '../../Plot/Plot';
import { AnimationSvgWrapper } from '../../utils/AnimationSvgWrapper';
import { ShapeGenerator } from '../ShapeGenerator';
import { ShapeRenderFn } from '../ShapeRenderFn';

const initialTransform = 'scaleY(0)';

const scale = keyframes({
  '0%': {
    transform: initialTransform,
  },
  '100%': {
    transform: 'scaleY(1)',
  },
});

const initialAnimation = `${scale} ${animationDurationMs}ms ${cubicBezierCurve} both`;
interface XYShapePath<TDataKeyType, TDatum> {
  path: string;
  key: TDataKeyType;
  baselinePath: string;
  animationStartPath: string;
  dataSeries: TDatum;
}
interface UseShapesParams<TDataKeyType extends DataKeyType, TPointType, TDatum> {
  /**
   * The data key of the active series, if any.
   */
  activeSeriesDataKey: TDataKeyType | undefined;
  /**
   * The color scale to use.
   */
  colorScale: ColorScale<TDataKeyType>;
  /**
   * A function (analogous to ReactDOM.createPortal) that renders the given content in the
   * associated portal.
   */
  createPortal: CreatePortalFn;
  /**
   * Whether the data has changed.
   */
  dataChanged: boolean;
  /**
   * The generator output.
   */
  generatorOutput: GeneratorOutput<TDataKeyType, TPointType, TDatum>;
  /**
   * Whether to enable animations.
   */
  isAnimated: boolean;
  /**
   * Whether the initial animation should be enabled when `isAnimated` is set to `true`.
   */
  isInitialAnimationEnabled: boolean;
  /**
   * Callback triggered when the initial animation is complete.
   */
  onInitialAnimationEnd?: React.AnimationEventHandler;
  /**
   * A map of custom intermediate paths used for interpolation by key
   * A series would only have an entry if it needs custom interpolation (number of points has changed in the series)
   */
  segmentedPathsByKey: Map<TDataKeyType, [segmentedInitialPath: string, segmentedTransitionPath: string]>;
  /**
   * A factory function that returns a shape generator.
   */
  shapeGenerator: ShapeGenerator<PartialSeriesPoint<[x: NumberCoercible, datum: unknown]>>;
  /**
   * A render function that produces the desired shape.
   */
  shapeRenderer: ShapeRenderFn;
  /**
   * The y-scale used for display.
   */
  yScale: NumberScale;
}

const produceInitialDataState = <TDatum,>(series: PartialSeriesPoint<TDatum>[]): PartialSeriesPoint<TDatum>[] => {
  const initialData = series.map((point) => {
    const zeroPoint = [0, point[1] === undefined ? undefined : 0] as const;
    return Object.assign(zeroPoint, { data: point.data });
  });
  return initialData;
};

/**
 * Returns a React node with elements for shapes to render representing the given data.
 */
export function useShapes<
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
>({
  activeSeriesDataKey,
  colorScale,
  createPortal,
  dataChanged,
  generatorOutput,
  isAnimated,
  isInitialAnimationEnabled,
  onInitialAnimationEnd,
  segmentedPathsByKey,
  shapeGenerator,
  shapeRenderer,
  yScale,
}: UseShapesParams<TDataKeyType, TPointType, TDatum>): React.ReactNode {
  type XYShapePathWithData = XYShapePath<TDataKeyType, GeneratorOutput<TDataKeyType, TPointType, TDatum>[number]>;
  const [isInitialAnimation, setIsInitialAnimation] = useState(isAnimated && isInitialAnimationEnabled);
  const { max: plotBottom } = useMemo(() => getRangePoints(yScale), [yScale]);

  const animationAtRest = useRef<boolean>(true);

  const shouldStartAnimation = isAnimated && dataChanged;

  useEffect(() => {
    if (shouldStartAnimation) {
      // animationAtRest is set to false when we run this hook with isAnimated=true
      // and only set to true once animation is at rest with the onRest hook of useTransition.
      // Anytime data changes and isAnimated = true, the animation will run to completion without
      // being cancelled out by later renders.
      animationAtRest.current = false;
    }
  }, [shouldStartAnimation]);

  const xyShapes = useMemo(() => {
    return generatorOutput
      .filter((series) => series.filter((datum) => datum[1] !== undefined).length > 0)
      .map((series) => ({
        path: shapeGenerator(series),
        key: series.key,
        baselinePath: shapeGenerator(produceInitialDataState(series)),
        animationStartPath: shapeGenerator(produceInitialDataState(series)),
        dataSeries: series,
      }));
  }, [generatorOutput, shapeGenerator]);

  const [transitionFn] = useTransition<XYShapePathWithData, { path: string }>(
    xyShapes,
    {
      keys: (xyShape: XYShapePathWithData) => xyShape.key,
      initial: (xyShape) => ({
        path: xyShape.path,
      }),
      from: (xyShape) => ({
        path: xyShape.animationStartPath,
      }),
      enter: (xyShape) => ({
        path: xyShape.path,
      }),
      update: (xyShape) => ({ path: xyShape.path }),
      leave: (xyShape) => ({
        path: xyShape.animationStartPath,
      }),
      default: {
        immediate: !isAnimated || (!dataChanged && animationAtRest.current),
        onRest: () => {
          animationAtRest.current = true;
        },
      },
    },
    [xyShapes, isAnimated, animationAtRest.current, dataChanged],
  );

  const shapes = useMemo(() => {
    const animatedShapes = transitionFn((props, item, _, i) => {
      const seriesDataKey = item.key;
      const color = colorScale.get(seriesDataKey);
      const seriesEmphasisLevel = getEmphasisLevel(activeSeriesDataKey, seriesDataKey);

      const animatedProps = { ...props };

      const intermediateSegmentedPaths = segmentedPathsByKey.get(seriesDataKey);

      if (intermediateSegmentedPaths && shouldStartAnimation) {
        if (item.dataSeries.length > 0) {
          const [startPath, finishPath] = intermediateSegmentedPaths;
          const newPath = new SpringValue(startPath, {
            to: finishPath,
          });
          animatedProps.path = newPath;
          props.path.set(item.path);
        }
      } else if (!shouldStartAnimation) {
        props.path.set(item.path);
      }

      const shape = shapeRenderer({
        color,
        emphasisLevel: seriesEmphasisLevel,
        path: animatedProps.path,
      });

      const handleOnAnimationEnd: React.AnimationEventHandler = (e) => {
        const isLastSeries = xyShapes.length - 1 === i;
        if (isLastSeries) {
          setIsInitialAnimation(false);
          onInitialAnimationEnd?.(e);
        }
      };

      const initialShapeRenderer =
        isInitialAnimation &&
        createPortal(
          <AnimationSvgWrapper
            animation={initialAnimation}
            transform={initialTransform}
            transformOrigin={`center ${plotBottom}px`}
            onAnimationEnd={handleOnAnimationEnd}
          >
            {shape}
          </AnimationSvgWrapper>,
        );

      return (
        <>
          <g opacity={isInitialAnimation ? 0 : undefined}>{shape}</g>
          {initialShapeRenderer}
        </>
      );
    });
    (animatedShapes.props as { children: React.ReactNode[] }).children.reverse();
    return animatedShapes;
  }, [
    colorScale,
    activeSeriesDataKey,
    segmentedPathsByKey,
    shouldStartAnimation,
    shapeRenderer,
    isInitialAnimation,
    createPortal,
    plotBottom,
    xyShapes.length,
    onInitialAnimationEnd,
  ]);

  return shapes;
}
